import React, { useEffect, useState, useCallback } from "react";
import classes from "./CompareResult.module.scss";
import TextForm from "../../atoms/text-form/TextForm";
import BreadCrumb from "../../molecules/breadcrumb/BreadCrumb";
import { useNavigate } from "react-router-dom";
import Header from "../../molecules/header/Header";
import { useLoginStatus } from "../../common/hooks/useLoginStatus";
import TopHeader from "../../organism/top-header/TopHeader";
import PointHeader from "../../organism/point-header/PointHeader";
import { fetchUser } from "../../api/UsersApi";
import teacherOk from "../../static/images/teacher-comment.png";
import teacherInterview from "../../static/images/teacher-interview.png";
import TeacherComment from "../../molecules/teacher-commnet/TeacherComment";
import SimpleButton from "../../atoms/simple-button/SimpleButton";
import TopSummary from "../../organism/top-summary/TopSummary";
import line from "../../static/images/dashed-line.png";
import headerImage from "../../static/images/compare/headerImage.png";
import {
  fetchUnregisteredCompareContent,
  postRegisteredCompareEvaluationContent,
  postUnregisteredCompareEvaluationContent,
  UnregistredUserCompare,
} from "../../api/UsersCompareApi";
import {
  digitsOnly,
  validateIntegerWith,
} from "../../common/utility/number.util";

import charSubHeader1 from "../../static/images/prepare/char-subheader1.png";
import charSubHeader2 from "../../static/images/compare/char-subheader2.png";
import charSubHeader3 from "../../static/images/compare/char-subheader3.png";
import charSubHeader5 from "../../static/images/compare/char-subheader5.png";
import bodyIconImage1 from "../../static/images/compare/compare-result-image-1.png";
import bodyIconImage2 from "../../static/images/compare/compare-result-image-2.png";
import bodyIconImage3 from "../../static/images/compare/compare-result-image-3.png";
import bodyIconImage4 from "../../static/images/compare/compare-result-image-4.png";
import bodyIconImage5 from "../../static/images/compare/compare-result-image-5.png";
import bodyPrepImage from "../../static/images/compare/compare-body-prep-image.png";
import bodyActionImage from "../../static/images/compare/compare-result-action-comment.png";
import { grade } from "../../atoms/education/EducationSetting";

// =====================
// サーバ返却の requestKey に合わせる
// =====================
type EvalKey =
  | "monthlySavings"
  | "monthlyInvestment"
  | "totalSavings"
  | "totalInvestment"
  | "monthlyLessonsFee";

type Form = Record<EvalKey, string>;

type Evaluation = {
  requestKey: EvalKey;
  evalutionComment: string | null;
  evalutionActionList: { evalutionActionComment: string }[] | null;
  requestValue: number | null;
  averageValue: number | null;
  evaluationImage: string | null;
};

const gradeLabelByValue = new Map(grade.map((item) => [item.value, item.name]));

function CompareResult() {
  const navigate = useNavigate();
  const { isLoggedIn } = useLoginStatus();
  const userApi = fetchUser();

  const fetchUnregisteredCompareApi = fetchUnregisteredCompareContent();
  const postUnregisteredCompareEvaluationApi =
    postUnregisteredCompareEvaluationContent();
  const postRegisteredCompareEvaluationApi =
    postRegisteredCompareEvaluationContent();

  const [compareContent, setCompareContent] = useState<UnregistredUserCompare>();

  const backToInput = () => {
    navigate("/comparenavi/input");
  };

  // requestKey ごとの評価結果を保持（pendingEvalKey なし）
  const [evaluationMap, setEvaluationMap] = useState<
    Partial<Record<EvalKey, Omit<Evaluation, "requestKey">>>
  >({});

  const [formValue, setFormValue] = useState<Form>({
    monthlySavings: "",
    monthlyInvestment: "",
    totalSavings: "",
    totalInvestment: "",
    monthlyLessonsFee: "",
  });

  // ログイン状態に応じてプロフィール取得元を切り替える
  // 会員登録前は unregistered API からデータを取得し、会員登録後は User API からデータを取得する
  useEffect(() => {
    if (isLoggedIn === null) return;
    if (isLoggedIn) {
      userApi.fetch();
      return;
    }
    fetchUnregisteredCompareApi.fetch();
  }, [isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn === null) return;

    const first = isLoggedIn
      ? (userApi.state?.data as any[] | undefined)?.[0]
      : (fetchUnregisteredCompareApi.state?.data as
          | UnregistredUserCompare[]
          | undefined)?.[0];
    if (!first) return;

    setCompareContent({
      postalCode: first.postalCode ?? null,
      childrenCount: first.childrenCount ?? null,
      birthYear: first.birthYear ?? null,
      annualSalary: first.annualSalary ?? null,
      children: first.children ?? null,
      childrenColumn1: first.childrenColumn1 ?? null,
      childrenColumn2: first.childrenColumn2 ?? null,
      childrenColumn3: first.childrenColumn3 ?? null,
      childrenColumn4: first.childrenColumn4 ?? null,
      childrenColumn5: first.childrenColumn5 ?? null,
    });
  }, [isLoggedIn, fetchUnregisteredCompareApi.state?.data, userApi.state?.data]);

  // ボタン押下で評価APIを呼ぶ（requestKey/name, requestValue/value）
  const onClickCompare = useCallback(
    (key: EvalKey) => {
      const api = isLoggedIn
        ? postRegisteredCompareEvaluationApi
        : postUnregisteredCompareEvaluationApi;
      api.post({
        requestKey: key,
        requestValue: formValue[key],
      });
    },
    [
      formValue,
      isLoggedIn,
      postRegisteredCompareEvaluationApi,
      postUnregisteredCompareEvaluationApi,
    ]
  );

  useEffect(() => {
    const st: any = isLoggedIn
      ? postRegisteredCompareEvaluationApi.state
      : postUnregisteredCompareEvaluationApi.state;
    const payload: any = st?.data; // { requestKey, evalutionComment, requestValue, averageValue, evalutionActionList }

    if (!payload?.requestKey) return;

    const key = payload.requestKey as EvalKey;

    setEvaluationMap((prev) => ({
      ...prev,
      [key]: {
        evalutionComment: payload.evalutionComment ?? null,
        evalutionActionList: payload.evalutionActionList ?? null,
        requestValue:
          payload.requestValue === undefined ? null : Number(payload.requestValue),
        averageValue:
          payload.averageValue === undefined ? null : Number(payload.averageValue),
        evaluationImage: payload.evaluationImage ?? null, // data URI

      },
    }));
  }, [
    isLoggedIn,
    postRegisteredCompareEvaluationApi.state,
    postUnregisteredCompareEvaluationApi.state,
  ]);

  // 表示用：評価コメント＋アクションを描画
  const renderEvaluation = (key: EvalKey) => {
    const ev = evaluationMap[key];
    return (
      <>
        <div className={classes.bodySummary}>
          <div className={classes.bodyComment}>
            {normalizeLineBreaks(ev?.evalutionComment)}
          </div>
        </div>
        <img className={classes.resultImage} src={bodyActionImage} alt="比較ナビ結果" />
        <div className={classes.bodySummary}>
          <div className={classes.bodyActionComment}>
            <ul>
              {(ev?.evalutionActionList ?? []).length > 0 ? (
                (ev?.evalutionActionList ?? []).map((a, i) => (
                  <li key={`${key}-action-${i}`}>
                    {normalizeLineBreaks(a.evalutionActionComment)}
                  </li>
                ))
              ) : (
                <li>—</li>
              )}
            </ul>
          </div>
        </div>
      </>
    );
  };

  const formatYen = (v: number | null | undefined) => {
    if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
    return new Intl.NumberFormat("ja-JP").format(Number(v));
  };

  const formatPostalCode = (v?: string | null) => {
    const digits = (v ?? "").replace(/\D/g, "").slice(0, 7);
    if (!digits) return "—";
    if (digits.length <= 3) return `〒${digits}`;
    return `〒${digits.slice(0, 3)}-${digits.slice(3)}`;
  };

  const formatBirthYear = (v?: string | null) => {
    if (!v) return "—";
    return `${v}年`;
  };

  const formatAnnualSalary = (v?: string | number | null) => {
    if (!v) return "—";
    const n = Number(String(v).replace(/,/g, ""));
    if (Number.isFinite(n)) {
      return `${new Intl.NumberFormat("ja-JP").format(n)}万円`;
    }
    return `${v}万円`;
  };

  const formatChildValue = (v?: string) => {
    if (!v) return "—";
    const label = gradeLabelByValue.get(v);
    if (label) return label;
    const digits = String(v).replace(/\D/g, "");
    if (digits.length === 4) return `${digits}年`;
    return v;
  };

  const childrenSource =
    compareContent?.children && compareContent.children.length > 0
      ? compareContent.children
      : [
          compareContent?.childrenColumn1,
          compareContent?.childrenColumn2,
          compareContent?.childrenColumn3,
          compareContent?.childrenColumn4,
          compareContent?.childrenColumn5,
        ]
          .map((gradeValue, index) =>
            gradeValue ? { order: index + 1, grade: gradeValue } : null
          )
          .filter(
            (child): child is { order: number; grade: string } => child != null
          );

  const childrenRows = childrenSource
    .filter((child) => child && child.grade)
    .sort((a, b) => {
      const aOrder = Number.isFinite(Number(a.order)) ? Number(a.order) : 0;
      const bOrder = Number.isFinite(Number(b.order)) ? Number(b.order) : 0;
      return aOrder - bOrder;
  });

  // 表示用ショートカット
  const evMonthlySavings = evaluationMap["monthlySavings"];
  const isMonthlySavingsReady = !!evaluationMap["monthlySavings"];

  const evTotalSavings = evaluationMap["totalSavings"];
  const isTotalSavingsReady = !!evaluationMap["totalSavings"];

  const evMonthlyInvestment = evaluationMap["monthlyInvestment"];
  const isMonthlyInvestmentReady = !!evaluationMap["monthlyInvestment"];

  const evTotalInvestment = evaluationMap["totalInvestment"];
  const isTotalInvestmentReady = !!evaluationMap["totalInvestment"];

  const evMonthlyLessonsFee = evaluationMap["monthlyLessonsFee"];
  const isMonthlyLessonsFeeReady = !!evaluationMap["monthlyLessonsFee"];


  const renderResultImage = (key: EvalKey) => {
    const src = evaluationMap[key]?.evaluationImage; // data URI
    if (!src) return null;
    return <img className={classes.resultImage} src={src} alt="比較ナビ結果" />;
  };

  const normalizeLineBreaks = (text?: string | null) => {
    if (!text) return "—";

    return text
      .replace(/<\/?br\s*\/?>/gi, "\n")
      .replace(/\\r\\n/g, "\n")
      .replace(/\\n/g, "\n")
      .replace(/\r\n|\r/g, "\n");
  };

  return (
    <div className={classes.container}>
      {isLoggedIn && <Header />}
      {isLoggedIn && <TopHeader currentMenuUrl="/diagnosis-list" />}
      {isLoggedIn && (
        <PointHeader aquiredPoint={userApi.state?.data?.[0]?.currentPoint ?? 0} />
      )}
      <div className={classes.body}>
        <div className={classes.headerImage}>
            <img
              src={headerImage}
              alt="比べてナビヘッダー"
            />
        </div>
        <div className={classes.header}>
          <TeacherComment
            image={teacherOk}
            comment={["あなたに似ている家庭と比べてみよう！"]}
          />
        </div>
        <div className={classes.profileContainer}>
          <div className={classes.profileRows}>
            <div className={classes.row}>
              <div className={classes.label}>お住まいの郵便番号</div>
              <div className={classes.value}>
                {formatPostalCode(compareContent?.postalCode)}
              </div>
            </div>
            {childrenRows.map((child, index) => {
              const order = Number.isFinite(Number(child.order))
                ? Number(child.order)
                : index + 1;
              return (
                <div className={classes.row} key={`child-${order}`}>
                  <div className={classes.label}>
                    {order}人目お子さま
                  </div>
                  <div className={classes.value}>{formatChildValue(child.grade)}</div>
                </div>
              );
            })}

            <div className={classes.row}>
              <div className={classes.label}>世帯主の生年</div>
              <div className={classes.value}>
                {formatBirthYear(compareContent?.birthYear)}
              </div>
            </div>
            <div className={classes.row}>
              <div className={classes.label}>世帯年収</div>
              <div className={classes.value}>
                {formatAnnualSalary(compareContent?.annualSalary)}
              </div>
            </div>
          </div>
          <div className={classes.profileButton} onClick={backToInput}>
            <SimpleButton
              label="情報を修正"
              color="white"
              textColor="black"
            />
          </div>
        </div>

        {/* ========== 1) 月々の貯金額 ========== */}
        <TopSummary leftLogo={charSubHeader1} mainTitle="月々の貯金額" />
        <div className={classes.compareContainer}>
          <img src={bodyIconImage1} alt="比較ナビ結果イメージ1" />
          <div className={classes.compareButtonArea}>
            <TextForm
              name="monthlySavings"
              value={formValue.monthlySavings}
              color="white"
              placeholder="例：50"
              inputWidth="30%"
              maxLength={5} // ★ maxlength:5
              suffix="000 円/月"
              validator={(v) =>
                validateIntegerWith(v, {
                  allowEmpty: false,   // 必須
                  min: 1,              // ★ min:1
                  max: 99999,          // ★ max:99999
                  label: "月々の貯金額",
                })
              }
              onChange={(args) => {
                const digits = digitsOnly(args.value, 5); // ★ 数字のみ + 5桁制限
                setFormValue((prev) => ({ ...prev, monthlySavings: digits }));
              }}
            />
            <SimpleButton
              label="比較する！"
              color="orange"
              textColor="black"
              width="80%"
              onClick={() => onClickCompare("monthlySavings")}
            />
          </div>
          {!isMonthlySavingsReady ? (
            <img src={bodyPrepImage} alt="比較ナビ結果未入力イメージ" />
          ) : (
            <>
              {/* 比較済みなら prep画像を出さず、結果エリアを表示 */}
              <div className={classes.compareBodyArea}>
                <div className={classes.bodyTitle}>みんなの「月々の貯金額」</div>
                <img className={classes.bodyLineImage} src={line} alt="line" />

                <div className={classes.bodySummary}>
                  <div className={classes.summaryBalloon}>
                    <div className={classes.summaryRow}>
                      <span className={classes.summaryLabel}>みんなの平均：</span>
                      <span className={classes.summaryValue}>
                        {formatYen(evMonthlySavings?.averageValue)}
                      </span>
                      <span className={classes.summaryUnit}>円</span>
                    </div>
                    <div className={classes.summaryRow}>
                      <span className={classes.summaryLabel}>あなた：</span>
                      <span className={classes.summaryValue}>
                        {formatYen(evMonthlySavings?.requestValue)}
                      </span>
                      <span className={classes.summaryUnit}>円</span>
                    </div>
                  </div>
                </div>
                {renderResultImage("monthlySavings")}
                {renderEvaluation("monthlySavings")}
              </div>
            </>
          )}
        </div>

        {/* ========== 2) 貯金総額 ========== */}
        <TopSummary leftLogo={charSubHeader2} mainTitle="貯金総額" />
        <div className={classes.header}>
          <TeacherComment
            image={teacherInterview}
            comment={["現金（銀行口座、自宅）で貯金している金額を入れてください！"]}
          />
        </div>
        <div className={classes.compareContainer}>
          <img src={bodyIconImage2} alt="比較ナビ結果イメージ2" />
          <div className={classes.compareButtonArea}>
            <TextForm
              name="totalSavings"
              value={formValue.totalSavings}
              color="white"
              placeholder="例：1000"
              inputWidth="30%"
              maxLength={5}
              suffix="万 円"
              validator={(v) =>
                validateIntegerWith(v, {
                  allowEmpty: false,   // 必須
                  min: 1,              // ★ min:1
                  max: 99999,          // ★ max:99999
                  label: "貯金総額",
                })
              }
              onChange={(args) => {
                const digits = digitsOnly(args.value, 5);
                setFormValue((prev) => ({ ...prev, totalSavings: digits }));
              }}
            />
            <SimpleButton
              label="比較する！"
              color="orange"
              textColor="black"
              width="80%"
              onClick={() => onClickCompare("totalSavings")}
            />
          </div>
          {!isTotalSavingsReady ? (
            <img src={bodyPrepImage} alt="比較ナビ結果未入力イメージ" />
          ) : (
           <div className={classes.compareBodyArea}>
            <div className={classes.bodyTitle}>みんなの「貯金総額」</div>
            <img className={classes.bodyLineImage} src={line} alt="line" />

            <div className={classes.bodySummary}>
              <div className={classes.summaryBalloon}>
                <div className={classes.summaryRow}>
                  <span className={classes.summaryLabel}>みんなの平均：</span>
                  <span className={classes.summaryValue}>
                    {formatYen(evTotalSavings?.averageValue)}
                  </span>
                  <span className={classes.summaryUnit}>万円</span>
                </div>
                <div className={classes.summaryRow}>
                  <span className={classes.summaryLabel}>あなた：</span>
                  <span className={classes.summaryValue}>
                    {formatYen(evTotalSavings?.requestValue)}
                  </span>
                  <span className={classes.summaryUnit}>万円</span>
                </div>
              </div>
            </div>
            {renderResultImage("totalSavings")}
            {renderEvaluation("totalSavings")}
          </div>
          )}
        </div>

        {/* ========== 3) 月々の投資額 ========== */}
        <TopSummary leftLogo={charSubHeader3} mainTitle="月々の投資額" />
        <div className={classes.compareContainer}>
          <img src={bodyIconImage3} alt="比較ナビ結果イメージ3" />
          <div className={classes.compareButtonArea}>
            <TextForm
              name="monthlyInvestment"
              value={formValue.monthlyInvestment}
              color="white"
              placeholder="例：50"
              inputWidth="30%"
              maxLength={5}
              suffix="000 円/月"
              onChange={(args) =>
                setFormValue((prev) => ({
                  ...prev,
                  monthlyInvestment: args.value,
                }))
              }
              validator={(v) =>
                validateIntegerWith(v, {
                  allowEmpty: false,   // 必須
                  min: 1,              // ★ min:1
                  max: 99999,          // ★ max:99999
                  label: "月々の投資額",
                })
              }
            />
            <SimpleButton
              label="比較する！"
              color="orange"
              textColor="black"
              width="80%"
              onClick={() => onClickCompare("monthlyInvestment")}
            />
          </div>
          {!isMonthlyInvestmentReady ? (
          <img src={bodyPrepImage} alt="比較ナビ結果未入力イメージ" />
          ) : (
          <div className={classes.compareBodyArea}>
            <div className={classes.bodyTitle}>みんなの「月々の投資額」</div>
            <img className={classes.bodyLineImage} src={line} alt="line" />
            <div className={classes.bodySummary}>
              <div className={classes.summaryBalloon}>
                <div className={classes.summaryRow}>
                  <span className={classes.summaryLabel}>みんなの平均：</span>
                  <span className={classes.summaryValue}>
                    {formatYen(evMonthlyInvestment?.averageValue)}
                  </span>
                  <span className={classes.summaryUnit}>円</span>
                </div>
                <div className={classes.summaryRow}>
                  <span className={classes.summaryLabel}>あなた：</span>
                  <span className={classes.summaryValue}>
                    {formatYen(evMonthlyInvestment?.requestValue)}
                  </span>
                  <span className={classes.summaryUnit}>円</span>
                </div>
              </div>
            </div>
            {renderResultImage("monthlyInvestment")}
            {renderEvaluation("monthlyInvestment")}
          </div>
          )}  
        </div>
        {/* ========== 4) 投資総額 ========== */}
        <TopSummary leftLogo={charSubHeader2} mainTitle="投資総額" />
        <div className={classes.header}>
          <TeacherComment
            image={teacherInterview}
            comment={["保険、株、投資信託、NISA、イデコの貯蓄金額を入れてください！"]}
          />
        </div>
        <div className={classes.compareContainer}>
          <img src={bodyIconImage4} alt="比較ナビ結果イメージ4" />
          <div className={classes.compareButtonArea}>
            <TextForm
              name="totalInvestment"
              value={formValue.totalInvestment}
              color="white"
              placeholder="例：1000"
              inputWidth="30%"
              maxLength={5}
              suffix="万 円"
              onChange={(args) =>
                setFormValue((prev) => ({
                  ...prev,
                  totalInvestment: args.value,
                }))
              }
              validator={(v) =>
                validateIntegerWith(v, {
                  allowEmpty: false,   // 必須
                  min: 1,              // ★ min:1
                  max: 99999,          // ★ max:99999
                  label: "投資総額",
                })
              }
            />
            <SimpleButton
              label="比較する！"
              color="orange"
              textColor="black"
              width="80%"
              onClick={() => onClickCompare("totalInvestment")}
            />
          </div>
          {!isTotalInvestmentReady ? (
          <img src={bodyPrepImage} alt="比較ナビ結果未入力イメージ" />
          ) : (
          <div className={classes.compareBodyArea}>
            <div className={classes.bodyTitle}>みんなの「投資総額」</div>
            <img className={classes.bodyLineImage} src={line} alt="line" />

            <div className={classes.bodySummary}>
              <div className={classes.summaryBalloon}>
                <div className={classes.summaryRow}>
                  <span className={classes.summaryLabel}>みんなの平均：</span>
                  <span className={classes.summaryValue}>
                    {formatYen(evTotalInvestment?.averageValue)}
                  </span>
                  <span className={classes.summaryUnit}>万円</span>
                </div>
                <div className={classes.summaryRow}>
                  <span className={classes.summaryLabel}>あなた：</span>
                  <span className={classes.summaryValue}>
                    {formatYen(evTotalInvestment?.requestValue)}
                  </span>
                  <span className={classes.summaryUnit}>万円</span>
                </div>
              </div>
            </div>
            {renderResultImage("totalInvestment")}
            {renderEvaluation("totalInvestment")}
          </div>
          )}
        </div>

        {/* ========== 5) 月々の習いごと費 ========== */}
        <TopSummary leftLogo={charSubHeader5} mainTitle="月々の習いごと費" />
        <div className={classes.compareContainer}>
          <img src={bodyIconImage5} alt="比較ナビ結果イメージ5" />
          <div className={classes.compareButtonArea}>
            <TextForm
              name="monthlyLessonsFee"
              value={formValue.monthlyLessonsFee}
              color="white"
              placeholder="例：50"
              inputWidth="30%"
              maxLength={5}
              suffix="000 円/月"
              onChange={(args) =>
                setFormValue((prev) => ({
                  ...prev,
                  monthlyLessonsFee: args.value,
                }))
              }
              validator={(v) =>
                validateIntegerWith(v, {
                  allowEmpty: false,   // 必須
                  min: 1,              // ★ min:1
                  max: 99999,          // ★ max:99999
                  label: "月々の習いごと費",
                })
              }
            />
            <SimpleButton
              label="比較する！"
              color="orange"
              textColor="black"
              width="80%"
              onClick={() => onClickCompare("monthlyLessonsFee")}
            />
          </div>
          {!isMonthlyLessonsFeeReady ? (
          <img src={bodyPrepImage} alt="比較ナビ結果未入力イメージ" />
          ) : (
          <div className={classes.compareBodyArea}>
            <div className={classes.bodyTitle}>みんなの「月々の習いごと費」</div>
            <img className={classes.bodyLineImage} src={line} alt="line" />

            <div className={classes.bodySummary}>
              <div className={classes.summaryBalloon}>
                <div className={classes.summaryRow}>
                  <span className={classes.summaryLabel}>みんなの平均：</span>
                  <span className={classes.summaryValue}>
                    {formatYen(evMonthlyLessonsFee?.averageValue)}
                  </span>
                  <span className={classes.summaryUnit}>円</span>
                </div>
                <div className={classes.summaryRow}>
                  <span className={classes.summaryLabel}>あなた：</span>
                  <span className={classes.summaryValue}>
                    {formatYen(evMonthlyLessonsFee?.requestValue)}
                  </span>
                  <span className={classes.summaryUnit}>円</span>
                </div>
              </div>
            </div>
            {renderResultImage("monthlyLessonsFee")}
            {renderEvaluation("monthlyLessonsFee")}
          </div>
          )}
        </div>

        <BreadCrumb
          data={[
            { displayName: "みらいいコンシェル", url: "/top" },
            { displayName: "くらべてナビ", url: "/comparenavi" },
          ]}
        />
      </div>
    </div>
  );
}

export default CompareResult;
